import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Home, Search, ClipboardList, User, LogOut, Plus, Map as MapIcon, Megaphone } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DayHireMark } from "@/lib/brand";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/is-admin").then(({ data }) => setIsAdmin(!!data.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  const isLocal = user?.role === "local";
  const NavItem = ({ to, icon: Icon, label, testid }) => {
    const active = location.pathname === to || (to !== "/feed" && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
          active ? "bg-[#D9FF3D] text-[#23272B]" : "text-[#F3EFFA]/85 hover:bg-white/10"
        }`}
      >
        <Icon size={18} />
        <span className="hidden md:inline">{label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10 bg-[#F3EFFA] text-[#23272B]">
      <header className="sticky top-0 z-40 bg-[#3E2A4E] border-b border-black/10 text-[#F3EFFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/feed" className="flex items-center" data-testid="nav-logo">
            <DayHireMark size={34} withWordmark className="[&_span]:text-[#F3EFFA]" />
          </Link>

          <nav className="hidden sm:flex items-center gap-1">
            <NavItem to="/feed" icon={Home} label="Feed" testid="nav-feed" />
            <NavItem to="/map" icon={MapIcon} label="Map" testid="nav-map" />
            <NavItem to="/laborers" icon={Search} label="Helpers" testid="nav-helpers" />
            <NavItem to="/my-tasks" icon={ClipboardList} label={isLocal ? "My Tasks" : "My Jobs"} testid="nav-mytasks" />
          </nav>

          <div className="flex items-center gap-2">
            {isLocal && (
              <Button
                onClick={() => navigate("/post")}
                data-testid="nav-post-task-btn"
                className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] rounded-full px-4 sm:px-5 btn-primary-glow font-bold"
              >
                <Plus size={16} className="sm:mr-1" />
                <span className="hidden sm:inline">Post Task</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="rounded-full ring-2 ring-transparent hover:ring-white/30 transition-all">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.avatar_url || undefined} />
                    <AvatarFallback className="bg-[#D9FF3D] text-[#23272B] font-black">
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold text-[#23272B]">{user?.name}</div>
                  <div className="text-xs text-[#23272B]/60 capitalize">{user?.role === "laborer" ? "helper" : user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User size={16} className="mr-2" /> My Profile
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/ads")} data-testid="menu-admin-ads">
                    <Megaphone size={16} className="mr-2" /> Ads Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-5 sm:py-10 pb-28 sm:pb-10">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 bg-[#3E2A4E] text-[#F3EFFA] border-t border-black/10 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5 py-1.5">
          {[
            { to: "/feed", icon: Home, label: "Feed", testid: "mnav-feed" },
            { to: "/map", icon: MapIcon, label: "Map", testid: "mnav-map" },
            { to: "/helpers", icon: Search, label: "Helpers", testid: "mnav-helpers" },
            { to: "/my-tasks", icon: ClipboardList, label: isLocal ? "Tasks" : "Jobs", testid: "mnav-mytasks" },
            { to: "/profile", icon: User, label: "Me", testid: "mnav-profile" },
          ].map(({ to, icon: Icon, label, testid }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex flex-col items-center justify-center gap-0.5 py-1 min-h-[44px] ${active ? "text-[#D9FF3D]" : "text-[#F3EFFA]/70"}`}
              >
                <Icon size={20} />
                <span className="text-[10px] font-semibold">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
